<template>
  <div class="chart-card" :class="{ 'selected': isSelected, 'full-screen': fullScreen }" @click="!fullScreen && handleCardClick()">
    <div class="chart-card-header chart-name-top">
      <h6 class="chart-card-title">{{ chartTitle }}</h6>
      <div class="chart-card-actions">
        <button class="chart-action-btn" @click.stop="handleAction">
          <span class="action-label">Edit in Studio</span>
        </button>
      </div>
    </div>
    <div class="chart-card-content">
      <div v-if="loading" class="chart-loading">Loading chart data...</div>
      <div v-else-if="error" class="chart-error">{{ error }}</div>
      <div v-else :ref="chartRef" class="chart-container"></div>
    </div>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import { getDisplayNameDashboard } from "@/utils/cache";

export default {
  name: "ChartCard",
  props: {
    chartTitle: {
      type: String,
      required: true,
    },
    chartData: {
      type: Object,
      default: null,
    },
    chartIndex: {
      type: Number,
      default: 0,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    fullScreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chartRef: `chart-${this.chartIndex}`,
      loading: false,
      error: null,
      tableData: null,
      resizeObserver: null,
      updatedXColumnDisplayName: '',
      updatedYColumnDisplayName: '',
      updatedGroupColumnDisplayName: '',
    };
  },
  computed: {
    tenantId() {
      return getTenantPrefix();
    },
  },
  async mounted() {
    // chartData prop structure: { title, data: chartMetadata, chartData: rows, index }
    // So we need to access this.chartData.data for chart info and this.chartData.chartData for rows
    console.log("ChartCard mounted:", {
      chartData: this.chartData,
      hasData: !!this.chartData?.data,
      hasChartData: !!this.chartData?.chartData,
      chartDataLength: this.chartData?.chartData?.length
    });
    
    if (this.chartData) {
      const chartMetadata = this.chartData.data || this.chartData;
      const preloadedData = this.chartData.chartData;
      
      if (preloadedData && Array.isArray(preloadedData) && preloadedData.length > 0) {
        // Data already loaded, use it directly
        console.log("ChartCard: Using preloaded data", preloadedData.length, "rows");
        this.tableData = preloadedData;
        // Fetch display names for axes before rendering
        await this.fetchDisplayNames();
        this.loading = false;
        this.$nextTick(() => {
          this.renderChart();
        });
      } else if (chartMetadata && chartMetadata.ID) {
        // Need to fetch data
        console.log("ChartCard: Fetching data for chart", chartMetadata.ID);
        await this.fetchChartData();
      } else {
        console.warn("ChartCard: No chart metadata or ID found", chartMetadata);
      }
    }
    
    // Add resize observer for chart container
    this.$nextTick(() => {
      this.setupResizeObserver();
    });
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    const chartRef = this.$refs[this.chartRef];
    if (chartRef) {
      const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
      if (chartDiv) {
        try {
          Plotly.purge(chartDiv);
        } catch (e) {
          // Ignore
        }
      }
    }
  },
  watch: {
    chartData: {
      handler: async function(newVal, oldVal) {
        const chartMetadata = newVal?.data || newVal;
        const preloadedData = newVal?.chartData;
        
        // Check if metadata changed significantly (would require data reload)
        const oldMetadata = oldVal?.data || oldVal;
        const metadataChanged = oldMetadata && (
          oldMetadata.XAxisColumn !== chartMetadata?.XAxisColumn ||
          oldMetadata.YAxisColumn !== chartMetadata?.YAxisColumn ||
          oldMetadata.ZAxisColumn !== chartMetadata?.ZAxisColumn ||
          oldMetadata.GroupColumn !== chartMetadata?.GroupColumn ||
          oldMetadata.TableType !== chartMetadata?.TableType ||
          oldMetadata.ChartType !== chartMetadata?.ChartType ||
          JSON.stringify(oldMetadata.Filter || {}) !== JSON.stringify(chartMetadata?.Filter || {})
        );
        
        // If metadata changed significantly, we need to reload data even if preloadedData exists
        // because the old data might not match the new metadata
        if (metadataChanged && chartMetadata && chartMetadata.ID) {
          // Metadata changed - reload data with new metadata
          await this.fetchChartData();
        } else if (preloadedData && Array.isArray(preloadedData) && preloadedData.length > 0) {
          // Use preloaded data (metadata didn't change significantly, or just name changed)
          this.tableData = preloadedData;
          // Fetch display names for axes before rendering
          await this.fetchDisplayNames();
          this.loading = false;
          this.$nextTick(() => {
            this.renderChart();
          });
        } else if (chartMetadata && chartMetadata.ID) {
          // No preloaded data - fetch it
          await this.fetchChartData();
        }
      },
      deep: true,
    },
  },
  methods: {
    async fetchChartData() {
      const chartMetadata = this.chartData?.data || this.chartData;
      if (!chartMetadata || !chartMetadata.ID) {
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        // Build filter query parameters from chart Filter object (same as DashboardView)
        const buildFilterQueryParams = (chartFilter) => {
          const queryParams = {};
          if (!chartFilter) return queryParams;
          
          if (chartFilter.asset && Array.isArray(chartFilter.asset) && chartFilter.asset.length) {
            queryParams.asset = JSON.stringify(chartFilter.asset);
          }
          if (chartFilter.field && Array.isArray(chartFilter.field) && chartFilter.field.length) {
            queryParams.field = JSON.stringify(chartFilter.field);
          }
          if (chartFilter.well && Array.isArray(chartFilter.well) && chartFilter.well.length) {
            queryParams.well = JSON.stringify(chartFilter.well);
          }
          if (chartFilter.reservoir && Array.isArray(chartFilter.reservoir) && chartFilter.reservoir.length) {
            queryParams.reservoir = JSON.stringify(chartFilter.reservoir);
          }
          if (chartFilter.fluidType && Array.isArray(chartFilter.fluidType) && chartFilter.fluidType.length) {
            queryParams.fluidType = JSON.stringify(chartFilter.fluidType);
          }
          if (chartFilter.sampleType && Array.isArray(chartFilter.sampleType) && chartFilter.sampleType.length) {
            queryParams.sampleType = JSON.stringify(chartFilter.sampleType);
          }
          if (chartFilter.fluidClassification && Array.isArray(chartFilter.fluidClassification) && chartFilter.fluidClassification.length) {
            queryParams.fluidClassification = JSON.stringify(chartFilter.fluidClassification);
          }
          if (chartFilter.fluidSampleID && Array.isArray(chartFilter.fluidSampleID) && chartFilter.fluidSampleID.length) {
            queryParams.fluidSampleID = JSON.stringify(chartFilter.fluidSampleID);
          }
          if (chartFilter.fluidSampleContainerID && Array.isArray(chartFilter.fluidSampleContainerID) && chartFilter.fluidSampleContainerID.length) {
            queryParams.fluidSampleContainerID = JSON.stringify(chartFilter.fluidSampleContainerID);
          }
          if (chartFilter.source && chartFilter.source !== "") {
            queryParams.source = `"${chartFilter.source}"`;
          }
          if (chartFilter.depth) {
            queryParams.depth = JSON.stringify(chartFilter.depth);
          }
          if (chartFilter.formationPressure) {
            queryParams.formationPressure = JSON.stringify(chartFilter.formationPressure);
          }
          if (chartFilter.formationTemperature) {
            queryParams.formationTemperature = JSON.stringify(chartFilter.formationTemperature);
          }
          if (chartFilter.sampleDate) {
            queryParams.sampleDate = JSON.stringify(chartFilter.sampleDate);
          }
          return queryParams;
        };
        
        // Build filter query parameters from chart Filter object
        const filterParams = buildFilterQueryParams(chartMetadata.Filter);
        const queryString = Object.entries(filterParams)
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join("&");
        
        // Build URL with filter query parameters if any exist
        const url = queryString 
          ? `public/api/charts/data/${chartMetadata.ID}?${queryString}`
          : `public/api/charts/data/${chartMetadata.ID}`;
        
        // Step 2: Call data API after getting chart info (with filters)
        const response = await api.get(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          const samples = response.data.data?.samples;
          
          let rows = null;
          
          // Handle different sample formats
          if (samples != null) {
            if (Array.isArray(samples)) {
              if (samples.length > 0) {
                rows = samples;
              }
            } else if (typeof samples === 'object' && 'rows' in samples && samples.rows) {
              rows = samples.rows;
            }
          }

          if (rows && rows.length > 0) {
            // Process rows similar to DashboardCreate
            this.tableData = rows.map((row) => {
              Object.keys(row).forEach((key) => {
                if (key + "_decimal" in row && row[key + "_decimal"] !== null) {
                  const numValue = parseFloat(row[key]);
                  if (!isNaN(numValue)) {
                    row[key] = numValue;
                  }
                }
              });
              return row;
            });
            
            // Fetch display names for axes
            await this.fetchDisplayNames();
            
            this.loading = false;
            // Step 3: Plot the chart
            this.$nextTick(() => {
              this.renderChart();
            });
          } else {
            this.loading = false;
            this.error = `Data is not available for this plot (${this.chartTitle})`;
          }
        } else {
          this.loading = false;
          this.error = "Failed to load chart data";
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
        this.loading = false;
        this.error = "Error loading chart data";
      }
    },

    renderChart() {
      const chartMetadata = this.chartData?.data || this.chartData;
      if (!chartMetadata || !this.tableData || this.tableData.length === 0) {
        console.log("ChartCard: Missing data for rendering", {
          hasChartData: !!this.chartData,
          hasChartMetadata: !!chartMetadata,
          hasTableData: !!this.tableData,
          tableDataLength: this.tableData?.length,
          chartDataStructure: this.chartData
        });
        return;
      }

      this.$nextTick(() => {
        const chartRef = this.$refs[this.chartRef];
        if (!chartRef) {
          console.error("ChartCard: Chart ref not found", this.chartRef);
          return;
        }

        const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
        if (!chartDiv) {
          console.error("ChartCard: Chart div not found");
          return;
        }

        // Ensure container has dimensions
        const containerWidth = chartDiv.offsetWidth || chartDiv.clientWidth || chartDiv.getBoundingClientRect().width;
        const containerHeight = chartDiv.offsetHeight || chartDiv.clientHeight || chartDiv.getBoundingClientRect().height;
        
        console.log("ChartCard: Container dimensions", {
          offsetWidth: chartDiv.offsetWidth,
          offsetHeight: chartDiv.offsetHeight,
          clientWidth: chartDiv.clientWidth,
          clientHeight: chartDiv.clientHeight,
          boundingRect: chartDiv.getBoundingClientRect(),
          computedWidth: containerWidth,
          computedHeight: containerHeight
        });
        
        if (containerWidth === 0 || containerHeight === 0) {
          console.warn("ChartCard: Container has no dimensions, waiting...");
          setTimeout(() => this.renderChart(), 100);
          return;
        }
        
        // Force explicit dimensions
        chartDiv.style.width = containerWidth + 'px';
        chartDiv.style.height = containerHeight + 'px';

        try {
          // Clean up any existing plot
          if (chartDiv.querySelector('.js-plotly-plot') || chartDiv.classList.contains('js-plotly-plot')) {
            Plotly.purge(chartDiv);
          }
        } catch (e) {
          console.warn("ChartCard: Purge error (ignoring)", e);
        }
        
        // Ensure container is empty and ready
        chartDiv.innerHTML = '';

        const traces = this.generateTraces();
        if (!traces || traces.length === 0) {
          // Check if Y-axis is empty to show specific error message
          const yColumns = chartMetadata.YAxisColumn 
            ? chartMetadata.YAxisColumn.split(",").map(col => col.trim()).filter(col => col) 
            : [];
          
          if (yColumns.length === 0) {
            this.error = "Y-Axis column is required. Please select a Y-Axis column.";
          } else if (!chartMetadata.XAxisColumn) {
            this.error = "X-Axis column is required. Please select an X-Axis column.";
          } else {
            this.error = "Unable to generate chart traces. Please check your axis selections and data.";
          }
          
          console.error("ChartCard: No traces generated", {
            chartMetadata: chartMetadata,
            tableDataLength: this.tableData?.length,
            XAxisColumn: chartMetadata?.XAxisColumn,
            YAxisColumn: chartMetadata?.YAxisColumn,
            yColumns: yColumns
          });
          return;
        }

        // Filter out any invalid traces (undefined, null, or missing required properties)
        const validTraces = traces.filter(trace => {
          if (!trace || typeof trace !== 'object') {
            return false;
          }
          // Ensure trace has required properties
          if (!trace.type || !trace.mode) {
            return false;
          }
          // Ensure x and y data exist and are arrays
          if (!Array.isArray(trace.x) || !Array.isArray(trace.y)) {
            return false;
          }
          // Ensure line property is either undefined or a valid object (not null)
          if (trace.line !== undefined && trace.line !== null && typeof trace.line !== 'object') {
            return false;
          }
          return true;
        });

        if (validTraces.length === 0) {
          console.warn("ChartCard: No valid traces to render", { originalTraces: traces });
          this.error = "No valid data to display";
          return;
        }

        console.log("ChartCard: Rendering chart with traces", validTraces.length, chartMetadata);

        const layout = this.generateLayout();
        const config = {
          responsive: true,
          displaylogo: false,
          displayModeBar: true,
          modeBarButtonsToRemove: ["lasso2d", "select2d"],
        };

        // Set axis scales
        if (chartMetadata.ChartType === "Scatter Plot" || chartMetadata.ChartType === "Line Plot") {
          layout.xaxis.type = chartMetadata.XAxisScale || "linear";
          layout.yaxis.type = chartMetadata.YAxisScale || "linear";
        } else if (chartMetadata.ChartType === "3D Scatter") {
          if (layout.scene) {
            layout.scene.xaxis.type = chartMetadata.XAxisScale || "linear";
            layout.scene.yaxis.type = chartMetadata.YAxisScale || "linear";
          }
        }

        console.log("ChartCard: About to render Plotly", {
          chartDiv: chartDiv,
          tracesCount: validTraces.length,
          layout: layout,
          containerWidth: containerWidth,
          containerHeight: containerHeight
        });

        Plotly.newPlot(chartDiv, validTraces, layout, config)
          .then(() => {
            console.log("ChartCard: Plotly.newPlot promise resolved");
            
            // Wait a bit for DOM to update
            setTimeout(() => {
              // Check if chartDiv itself has the class or if it's a child
              const plotElement = chartDiv.classList.contains('js-plotly-plot') 
                ? chartDiv 
                : chartDiv.querySelector('.js-plotly-plot') || chartDiv.querySelector('.plotly');
              
              console.log("ChartCard: Checking for plot element", {
                chartDivClasses: chartDiv.className,
                chartDivChildren: chartDiv.children.length,
                hasPlotlyClass: chartDiv.classList.contains('js-plotly-plot'),
                plotElement: plotElement,
                chartDivHTML: chartDiv.innerHTML.substring(0, 200)
              });
              
              if (plotElement || chartDiv.classList.contains('js-plotly-plot')) {
                const elementToCheck = plotElement || chartDiv;
                console.log("ChartCard: Chart rendered successfully", {
                  element: elementToCheck,
                  plotWidth: elementToCheck.offsetWidth,
                  plotHeight: elementToCheck.offsetHeight,
                  plotDisplay: window.getComputedStyle(elementToCheck).display,
                  plotVisibility: window.getComputedStyle(elementToCheck).visibility,
                  computedStyles: {
                    width: window.getComputedStyle(elementToCheck).width,
                    height: window.getComputedStyle(elementToCheck).height,
                    position: window.getComputedStyle(elementToCheck).position
                  }
                });
                
                // Resize to ensure proper display
                Plotly.Plots.resize(chartDiv).catch((err) => {
                  console.warn("ChartCard: Resize error", err);
                });
                
                // Force SVG visibility - check if SVG exists and has dimensions
                const svg = elementToCheck.querySelector('svg');
                if (svg) {
                  const svgChildren = svg.querySelectorAll('*');
                  console.log("ChartCard: SVG found", {
                    svgWidth: svg.getAttribute('width'),
                    svgHeight: svg.getAttribute('height'),
                    svgViewBox: svg.getAttribute('viewBox'),
                    svgChildrenCount: svgChildren.length,
                    hasPaths: svg.querySelectorAll('path').length,
                    hasLines: svg.querySelectorAll('line').length,
                    hasCircles: svg.querySelectorAll('circle').length,
                    svgDisplay: window.getComputedStyle(svg).display,
                    svgVisibility: window.getComputedStyle(svg).visibility,
                    svgOpacity: window.getComputedStyle(svg).opacity,
                    parentOverflow: window.getComputedStyle(elementToCheck.parentElement).overflow,
                    parentDisplay: window.getComputedStyle(elementToCheck.parentElement).display
                  });
                  
                  // Force SVG to be visible with !important via setAttribute
                  svg.setAttribute('style', 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 100% !important; height: 100% !important;');
                  
                  // Ensure SVG has proper dimensions
                  const svgWidth = svg.getAttribute('width');
                  const svgHeight = svg.getAttribute('height');
                  
                  if (!svgWidth || svgWidth === '0' || svgWidth === '0px') {
                    svg.setAttribute('width', containerWidth);
                  }
                  if (!svgHeight || svgHeight === '0' || svgHeight === '0px') {
                    svg.setAttribute('height', containerHeight);
                  }
                  
                  // Ensure viewBox is set
                  if (!svg.getAttribute('viewBox') && svgWidth && svgHeight) {
                    svg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
                  }
                  
                  // Also check and fix plot-container
                  const plotContainer = elementToCheck.querySelector('.plot-container');
                  if (plotContainer) {
                    plotContainer.style.cssText = 'width: 100% !important; height: 100% !important; display: block !important; visibility: visible !important; opacity: 1 !important;';
                  }
                  
                  // Force all parent containers to be visible
                  let parent = elementToCheck.parentElement;
                  while (parent && parent !== document.body) {
                    const parentStyle = window.getComputedStyle(parent);
                    if (parentStyle.display === 'none' || parentStyle.visibility === 'hidden' || parentStyle.opacity === '0') {
                      parent.style.cssText += 'display: block !important; visibility: visible !important; opacity: 1 !important;';
                    }
                    if (parentStyle.overflow === 'hidden') {
                      parent.style.overflow = 'visible';
                    }
                    parent = parent.parentElement;
                  }
                  
                  // Force all child elements to be visible
                  const allChildren = elementToCheck.querySelectorAll('*');
                  allChildren.forEach(child => {
                    const computedStyle = window.getComputedStyle(child);
                    if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || computedStyle.opacity === '0') {
                      child.style.cssText += 'display: block !important; visibility: visible !important; opacity: 1 !important;';
                    }
                  });
                } else {
                  console.error("ChartCard: SVG not found in plot element");
                }
              } else {
                console.error("ChartCard: Plot element not found after rendering", {
                  chartDiv: chartDiv,
                  innerHTML: chartDiv.innerHTML.substring(0, 500),
                  children: Array.from(chartDiv.children).map(c => ({
                    tagName: c.tagName,
                    className: c.className,
                    id: c.id
                  }))
                });
              }
            }, 100);
          })
          .catch((error) => {
            console.error("ChartCard: Error creating plot", error, {
              errorStack: error.stack,
              traces: traces,
              layout: layout
            });
            this.error = "Error rendering chart: " + (error.message || "Unknown error");
          });
      });
    },

    generateTraces() {
      const chart = this.chartData?.data || this.chartData;
      if (!chart || !this.tableData || this.tableData.length === 0) {
        return [];
      }
      const traceType = this.getTraceType(chart.ChartType);
      const traceMode = this.getTraceMode(chart.ChartType);
      
      const yColumns = chart.YAxisColumn 
        ? chart.YAxisColumn.split(",").map(col => col.trim()).filter(col => col) 
        : [];

      // Check if required axis columns are present
      if (!chart.XAxisColumn) {
        console.warn("ChartCard: Missing XAxisColumn", {
          XAxisColumn: chart.XAxisColumn,
          YAxisColumn: chart.YAxisColumn
        });
        return [];
      }
      
      // Handle empty Y-axis gracefully
      if (yColumns.length === 0) {
        console.warn("ChartCard: YAxisColumn is empty", {
          XAxisColumn: chart.XAxisColumn,
          YAxisColumn: chart.YAxisColumn,
          yColumns: yColumns
        });
        // Return empty array - renderChart will handle this and show error
        return [];
      }

      // Handle grouped charts
      if (chart.GroupColumn && chart.YAxisColumn) {
        // Remove GroupColumn from yColumns if it's the last one
        if (yColumns.length > 1 && yColumns[yColumns.length - 1] === chart.GroupColumn) {
          yColumns.pop();
        }

        const colorGroupData = this.tableData.map(item => item[chart.GroupColumn]);
        const uniqueValues = [...new Set(colorGroupData.filter(item => item !== undefined && item !== null))];
        const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];
        
        const traces = [];
        uniqueValues.forEach((group, idx) => {
          const groupData = this.tableData.filter(row => row[chart.GroupColumn] === group);
          const xData = groupData.map(row => {
            const val = row[chart.XAxisColumn];
            return val != null && val !== '' ? val : null;
          }).filter(v => v != null);
          
          const yData = groupData.map(row => {
            const val = row[yColumns[0]];
            if (val == null || val === '') return null;
            const numVal = typeof val === 'number' ? val : parseFloat(val);
            return !isNaN(numVal) ? numVal : null;
          }).filter(v => v != null);
          
          if (xData.length > 0 && yData.length > 0 && xData.length === yData.length) {
            // Sort by x-axis for line plots
            const combined = xData.map((x, i) => ({ x, y: yData[i] }));
            const isNumeric = xData.every(x => typeof x === 'number' || !isNaN(parseFloat(x)));
            if (isNumeric && traceMode === 'lines') {
              combined.sort((a, b) => {
                const aVal = typeof a.x === 'number' ? a.x : parseFloat(a.x);
                const bVal = typeof b.x === 'number' ? b.x : parseFloat(b.x);
                return aVal - bVal;
              });
            }
            const sortedX = combined.map(item => item.x);
            const sortedY = combined.map(item => item.y);
            
            // Use display name for legend if available
            const groupDisplayName = this.updatedGroupColumnDisplayName 
              ? String(group) 
              : String(group);
            
            const trace = {
              x: sortedX,
              y: sortedY,
              type: traceType,
              mode: traceMode,
              name: groupDisplayName,
            };
            
            // Only add marker if needed
            if (traceMode === 'markers' || traceMode === 'lines+markers') {
              trace.marker = {
                size: 8,
                color: colors[idx % colors.length],
              };
            }
            
            // Only add line if needed (don't set to undefined)
            if (traceMode === 'lines' || traceMode === 'lines+markers') {
              trace.line = {
                width: 2,
                color: colors[idx % colors.length],
              };
            }
            
            traces.push(trace);
          }
        });
        return traces;
      } else {
        // Single trace
        const xData = this.tableData.map(row => {
          const val = row[chart.XAxisColumn];
          return val != null && val !== '' ? val : null;
        }).filter(v => v != null);
        
        const yData = this.tableData.map(row => {
          const val = row[yColumns[0]];
          if (val == null || val === '') return null;
          const numVal = typeof val === 'number' ? val : parseFloat(val);
          return !isNaN(numVal) ? numVal : null;
        }).filter(v => v != null);
        
        if (xData.length > 0 && yData.length > 0 && xData.length === yData.length) {
          // Sort by x-axis for line plots
          const combined = xData.map((x, i) => ({ x, y: yData[i] }));
          const isNumeric = xData.every(x => typeof x === 'number' || !isNaN(parseFloat(x)));
          if (isNumeric && traceMode === 'lines') {
            combined.sort((a, b) => {
              const aVal = typeof a.x === 'number' ? a.x : parseFloat(a.x);
              const bVal = typeof b.x === 'number' ? b.x : parseFloat(b.x);
              return aVal - bVal;
            });
          }
          const sortedX = combined.map(item => item.x);
          const sortedY = combined.map(item => item.y);
          
          const trace = {
            x: sortedX,
            y: sortedY,
            type: traceType,
            mode: traceMode,
          };
          
          // Only add marker if needed
          if (traceMode === 'markers' || traceMode === 'lines+markers') {
            trace.marker = {
              size: 8,
              color: '#4ECDC4',
            };
          }
          
          // Only add line if needed (don't set to undefined)
          if (traceMode === 'lines' || traceMode === 'lines+markers') {
            trace.line = {
              width: 2,
              color: '#4ECDC4',
            };
          }
          
          return [trace];
        }
      }

      return [];
    },

    generateLayout() {
      const chart = this.chartData?.data || this.chartData;
      if (!chart) {
        return {};
      }
      const yColumns = chart.YAxisColumn 
        ? chart.YAxisColumn.split(",").map(col => col.trim()).filter(col => col) 
        : [];

      const xAxisTitle = this.formatAxisTitle(
        this.updatedXColumnDisplayName || chart.XAxisColumn || "X Axis",
        this.getUOMForProperty(chart.XAxisColumn)
      );
      
      const yAxisTitle = this.formatAxisTitle(
        this.updatedYColumnDisplayName || yColumns[0] || "Y Axis",
        this.getUOMForProperty(yColumns[0])
      );

      const baseLayout = {
        autosize: true,
        margin: { l: 40, r: 20, t: 20, b: 55 },
        xaxis: {
          title: { text: xAxisTitle },
          showgrid: true,
          gridcolor: '#EFEFEF',
          tickangle: -45,
          tickfont: { size: 10 },
        },
        yaxis: {
          title: { text: yAxisTitle },
          showgrid: true,
          gridcolor: '#EFEFEF',
          tickfont: { size: 10 },
        },
        legend: {
          visible: chart.GroupColumn ? true : false,
          title: {
            text: this.updatedGroupColumnDisplayName || chart.GroupColumn || ''
          },
          x: 1.02,
          y: 1,
          xanchor: 'left',
          yanchor: 'top',
        },
      };

      if (chart.ChartType === "3D Scatter") {
        const zAxisTitle = this.formatAxisTitle(
          chart.ZAxisColumn || "Z",
          this.getUOMForProperty(chart.ZAxisColumn)
        );
        
        return {
          ...baseLayout,
          scene: {
            xaxis: { 
              title: { text: xAxisTitle },
              tickfont: { size: 10 },
            },
            yaxis: { 
              title: { text: yAxisTitle },
              tickfont: { size: 10 },
            },
            zaxis: { 
              title: { text: zAxisTitle },
              tickfont: { size: 10 },
            },
          },
        };
      }

      return baseLayout;
    },

    getTraceType(chartType) {
      const typeMap = {
        'Scatter Plot': 'scatter',
        'Line Plot': 'scatter',
        'Bar Chart': 'bar',
        'Histogram': 'histogram',
        '2D Histogram': 'histogram2d',
        '3D Scatter': 'scatter3d',
      };
      return typeMap[chartType] || 'scatter';
    },

    getTraceMode(chartType) {
      if (chartType === 'Line Plot') {
        return 'lines';
      } else if (chartType === 'Scatter Plot') {
        return 'markers';
      } else if (chartType === 'Bar Chart') {
        return null;
      }
      return 'markers';
    },

    async fetchDisplayNames() {
      const chartMetadata = this.chartData?.data || this.chartData;
      if (!chartMetadata || !this.tableData || this.tableData.length === 0) {
        return;
      }

      try {
        // Get X-axis display name
        const xAxisColumn = chartMetadata.XAxisColumn;
        if (xAxisColumn) {
          const xColumnDisplayName = await getDisplayNameDashboard(
            chartMetadata.TableType,
            xAxisColumn
          );
          this.updatedXColumnDisplayName = xColumnDisplayName.displayName;
        }

        // Get Y-axis display names
        const yColumns = chartMetadata.YAxisColumn 
          ? chartMetadata.YAxisColumn.split(",").map(col => col.trim()).filter(col => col) 
          : [];
        
        // Remove GroupColumn from yColumns if it's the last one
        if (chartMetadata.GroupColumn && yColumns.length > 1 && yColumns[yColumns.length - 1] === chartMetadata.GroupColumn) {
          yColumns.pop();
        }

        if (yColumns.length > 0) {
          const yColumnDisplayNames = await Promise.all(
            yColumns.map((column) => getDisplayNameDashboard(chartMetadata.TableType, column))
          );
          this.updatedYColumnDisplayName = yColumnDisplayNames
            .map(item => item.displayName)
            .join(', ');
        }

        // Get Group column display name
        if (chartMetadata.GroupColumn) {
          const groupColumn = chartMetadata.GroupColumn;
          const capitalizedGroupColumn = groupColumn.charAt(0) + groupColumn.slice(1);
          const groupColumnDisplayName = await getDisplayNameDashboard(
            chartMetadata.TableType,
            capitalizedGroupColumn
          );
          this.updatedGroupColumnDisplayName = groupColumnDisplayName.displayName;
        } else {
          this.updatedGroupColumnDisplayName = "";
        }
      } catch (error) {
        console.error("ChartCard: Error fetching display names", error);
      }
    },

    getUOMForProperty(columnName) {
      // Get UOM from the first row of table data if available
      if (this.tableData && this.tableData.length > 0) {
        const firstRow = this.tableData[0];
        const uomKey = `${columnName}_UOM`;
        if (firstRow && firstRow[uomKey] && firstRow[uomKey] !== 'data-not-available') {
          return firstRow[uomKey];
        }
      }
      return null;
    },

    formatAxisTitle(displayName, uom) {
      // Format axis title with UOM if available
      if (uom && uom.trim() !== '') {
        return `${displayName} (${uom})`;
      }
      return displayName;
    },

    handleAction() {
      this.$emit("chart-action", this.chartIndex);
    },
    handleCardClick() {
      // Emit selection event only if not in full-screen mode
      if (!this.fullScreen) {
        this.$emit("chart-selected", this.chartIndex);
      }
    },

    setupResizeObserver() {
      const chartRef = this.$refs[this.chartRef];
      if (!chartRef) return;

      const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
      if (!chartDiv) return;

      // Use ResizeObserver to handle container size changes
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          if (chartDiv.querySelector('.js-plotly-plot')) {
            Plotly.Plots.resize(chartDiv).catch(() => {});
          }
        });
        this.resizeObserver.observe(chartDiv);
      } else {
        // Fallback for browsers without ResizeObserver
        window.addEventListener('resize', () => {
          if (chartDiv.querySelector('.js-plotly-plot')) {
            Plotly.Plots.resize(chartDiv).catch(() => {});
          }
        });
      }
    },
  },
};
</script>

<style scoped>
@import '../../assets/Styling/agenticDashboard/chartCard.css';
</style>

